// Image search cache utility
// Provides in-memory and localStorage caching for image searches (Unsplash, etc.)

const CACHE_KEY_PREFIX = 'image_search_';
const CACHE_PHOTO_PREFIX = 'photo_data_';
const CACHE_EXPIRY_DAYS = 30; // Cache for 30 days

interface CacheEntry {
  imageUrls: string[];
  timestamp: number;
}

interface PhotoCacheEntry {
  photos: any[];
  timestamp: number;
}

// In-memory cache
const memoryCache = new Map<string, string[]>();
const photoCache = new Map<string, any[]>();

/**
 * Get cached image URLs for an item name (from image search services)
 */
export function getCachedImages(itemName: string): string[] | null {
  const normalizedName = itemName.trim().toLowerCase();
  
  // Check memory cache first (fastest)
  if (memoryCache.has(normalizedName)) {
    return memoryCache.get(normalizedName)!;
  }
  
  // Check localStorage (slower but persistent)
  try {
    const cacheKey = CACHE_KEY_PREFIX + normalizedName;
    const cached = localStorage.getItem(cacheKey);
    
    if (cached) {
      const entry: CacheEntry = JSON.parse(cached);
      
      // Check if cache is expired
      const now = Date.now();
      const expiryTime = entry.timestamp + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      
      if (now < expiryTime) {
        // Valid cache - store in memory for faster access
        memoryCache.set(normalizedName, entry.imageUrls);
        return entry.imageUrls;
      } else {
        // Expired - remove from localStorage
        localStorage.removeItem(cacheKey);
      }
    }
  } catch (error) {
    console.error('Error reading from cache:', error);
  }
  
  return null;
}

/**
 * Cache image URLs for an item name (from image search services)
 */
export function setCachedImages(itemName: string, imageUrls: string[]): void {
  const normalizedName = itemName.trim().toLowerCase();
  
  // Store in memory cache
  memoryCache.set(normalizedName, imageUrls);
  
  // Store in localStorage
  try {
    const cacheKey = CACHE_KEY_PREFIX + normalizedName;
    const entry: CacheEntry = {
      imageUrls,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(entry));
  } catch (error) {
    console.error('Error writing to cache:', error);
    // If localStorage is full, try to clear old entries
    clearExpiredCache();
  }
}

/**
 * Clear expired cache entries from localStorage
 */
export function clearExpiredCache(): void {
  try {
    const now = Date.now();
    const keysToRemove: string[] = [];
    
    // Find expired entries
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        const cached = localStorage.getItem(key);
        if (cached) {
          try {
            const entry: CacheEntry = JSON.parse(cached);
            const expiryTime = entry.timestamp + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
            
            if (now >= expiryTime) {
              keysToRemove.push(key);
            }
          } catch {
            // Invalid entry - mark for removal
            keysToRemove.push(key);
          }
        }
      }
    }
    
    // Remove expired entries
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    console.log(`Cleared ${keysToRemove.length} expired image search cache entries`);
  } catch (error) {
    console.error('Error clearing expired cache:', error);
  }
}

// clearAllCache function removed - not used. If needed for debugging, check git history

/**
 * Cache photo data (full Unsplash photo objects with metadata)
 */
export function setCachedPhotoData(itemName: string, photos: any[]): void {
  const normalizedName = itemName.trim().toLowerCase();
  
  // Store in memory cache
  photoCache.set(normalizedName, photos);
  
  // Store in localStorage
  try {
    const cacheKey = CACHE_PHOTO_PREFIX + normalizedName;
    const entry: PhotoCacheEntry = {
      photos,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(entry));
  } catch (error) {
    console.error('Error caching photo data:', error);
  }
}

/**
 * Get cached photo data (full Unsplash photo objects)
 */
export function getCachedPhotoData(itemName: string): any[] | null {
  const normalizedName = itemName.trim().toLowerCase();
  
  // Check memory cache first
  if (photoCache.has(normalizedName)) {
    return photoCache.get(normalizedName)!;
  }
  
  // Check localStorage
  try {
    const cacheKey = CACHE_PHOTO_PREFIX + normalizedName;
    const cached = localStorage.getItem(cacheKey);
    
    if (cached) {
      const entry: PhotoCacheEntry = JSON.parse(cached);
      
      // Check if cache is expired
      const now = Date.now();
      const expiryTime = entry.timestamp + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      
      if (now < expiryTime) {
        // Valid cache - store in memory
        photoCache.set(normalizedName, entry.photos);
        return entry.photos;
      } else {
        // Expired - remove
        localStorage.removeItem(cacheKey);
      }
    }
  } catch (error) {
    console.error('Error reading photo cache:', error);
  }
  
  return null;
}
